import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Combobox,
  ConfirmDialog,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { useCatalogItemPicker } from '../catalog/useCatalogItemPicker';
import { settingsApi } from '../settings/settingsApi';
import { suppliersApi } from '../suppliers/suppliersApi';
import { marketlistApi, marketListPath } from './marketlistApi';
import './marketlist.css';

let tmpId = 0;
const tmpKey = () => `tmp-${++tmpId}`;

const newQuote = (over = {}) => ({ key: tmpKey(), supplierId: '', unitPrice: '', note: '', ...over });

const newLine = (over = {}) => ({
  key: tmpKey(),
  itemId: '',
  description: '',
  sku: '',
  uomId: '',
  parLevel: null,
  onHand: null,
  suggestedQuantity: null,
  orderQuantity: '',
  costCenter: '',
  glAccount: '',
  notes: '',
  quotes: [],
  assignedSupplierId: '',
  ...over,
});

// A row only counts as "real" once it references an item or has a description.
const isBlankLine = (l) => !l.itemId && !l.description.trim();

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

export default function MarketListFormPage() {
  const { id: routeRef } = useParams();
  const editing = Boolean(routeRef);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { activePropertyId, activeProperty, properties } = useProperty();

  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState('Draft');
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [submitOnFinalize, setSubmitOnFinalize] = useState(true);

  const inProgress = status === 'InProgress';

  const itemPicker = useCatalogItemPicker({ propertyId: activePropertyId });
  const items = itemPicker.items;
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState('');
  const [marketDate, setMarketDate] = useState(today);
  const [locationId, setLocationId] = useState('');
  const [currency, setCurrency] = useState('');
  const [fallbackCurrency, setFallbackCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([newLine()]);

  const loadLookups = useCallback(async () => {
    try {
      const [us, locs, sups, eff] = await Promise.all([
        catalogApi.listUnits(),
        catalogApi.listLocations(activePropertyId),
        suppliersApi.list({ pageSize: 500, status: 'Active' }).catch(() => null),
        settingsApi.getEffective(activePropertyId).catch(() => null),
      ]);
      setUnits(us || []);
      setLocations(locs || []);
      setSuppliers(sups?.items || (Array.isArray(sups) ? sups : []));
      setCostCenters((eff?.costCenters || []).filter((c) => c.isActive !== false));
      setGlAccounts((eff?.glAccounts || []).filter((g) => g.isActive !== false));
      setFallbackCurrency(eff?.defaultCurrency || 'USD');
    } catch {
      // Non-fatal; the form still works with free-text lines and USD.
    }
  }, [activePropertyId]);

  const loadMarketList = useCallback(async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const m = await marketlistApi.get(routeRef);
      setEditingId(m.id);
      if (m.status !== 'Draft' && m.status !== 'InProgress') {
        toast.error(t('Only draft or in-progress market lists can be edited.'));
        navigate(marketListPath(m));
        return;
      }
      setStatus(m.status);
      setTitle(m.title || '');
      setMarketDate(m.marketDate ? m.marketDate.slice(0, 10) : today);
      setLocationId(m.locationId || '');
      setCurrency(m.currency || 'USD');
      setNotes(m.notes || '');
      setLines([
        ...(m.lines || []).map((l) => newLine({
          itemId: l.itemId || '',
          description: l.description || '',
          sku: l.sku || '',
          uomId: l.uomId || '',
          parLevel: l.parLevel ?? null,
          onHand: l.onHand ?? null,
          suggestedQuantity: l.suggestedQuantity ?? null,
          orderQuantity: l.orderQuantity ?? '',
          costCenter: l.costCenter || '',
          glAccount: l.glAccountCode || '',
          notes: l.notes || '',
          quotes: (l.quotes || []).map((q) => newQuote({
            supplierId: q.supplierId,
            unitPrice: q.unitPrice?.amount ?? '',
            note: q.note || '',
          })),
          assignedSupplierId: l.assignedSupplierId || '',
        })),
        newLine(),
      ]);
    } catch (err) {
      toast.error(t('Could not load market list'), { description: err?.message });
      navigate('/market-list');
    } finally {
      setLoading(false);
    }
  }, [editing, routeRef, navigate, toast, today, t]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadMarketList(); }, [loadMarketList]);

  // Pull in items an existing list references but the first catalog page didn't include.
  useEffect(() => {
    itemPicker.ensureIds([...new Set(lines.map((l) => l.itemId).filter(Boolean))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  // The market-list currency is the property's local currency of record — resolved from the
  // selected outlet's property, then the active property, then the org's only property.
  const localCurrency = useMemo(() => {
    const loc = locations.find((l) => l.id === locationId);
    const outletProp = loc?.propertyId ? properties.find((p) => p.id === loc.propertyId) : null;
    if (outletProp?.baseCurrency) return outletProp.baseCurrency;
    if (activeProperty?.baseCurrency) return activeProperty.baseCurrency;
    if (properties.length === 1 && properties[0]?.baseCurrency) return properties[0].baseCurrency;
    return fallbackCurrency;
  }, [locations, locationId, properties, activeProperty, fallbackCurrency]);

  useEffect(() => {
    if (localCurrency) setCurrency(localCurrency);
  }, [localCurrency]);

  const itemOptions = itemPicker.options;
  const uomOptions = useMemo(
    () => [{ value: '', label: '—' }, ...units.map((u) => ({ value: u.id, label: u.code || u.name }))],
    [units],
  );
  const supplierOptions = useMemo(
    () => [{ value: '', label: t('— Select vendor —') }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))],
    [suppliers, t],
  );
  const supplierName = useCallback((sid) => suppliers.find((s) => s.id === sid)?.name || sid, [suppliers]);
  const locationOptions = useMemo(
    () => [{ value: '', label: t('— Select outlet —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );
  const lineCostCenterOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...costCenters.map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const lineGlAccountOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...glAccounts.map((g) => ({ value: g.code, label: g.name ? `${g.code} · ${g.name}` : g.code }))],
    [glAccounts, t],
  );

  // Keep exactly one trailing blank row so users can always add the next item inline.
  const withTrailingBlank = (next) => {
    const trimmed = [...next];
    while (trimmed.length > 1 && isBlankLine(trimmed[trimmed.length - 1]) && isBlankLine(trimmed[trimmed.length - 2])) {
      trimmed.pop();
    }
    if (trimmed.length === 0 || !isBlankLine(trimmed[trimmed.length - 1])) trimmed.push(newLine());
    return trimmed;
  };

  const updateLine = (key, patch) =>
    setLines((prev) => withTrailingBlank(prev.map((l) => (l.key === key ? { ...l, ...patch } : l))));
  const removeLine = (key) => setLines((prev) => withTrailingBlank(prev.filter((l) => l.key !== key)));

  // Fill (or clear) the catalog item on a line, auto-coding from the item's defaults
  // and prefilling vendor quotes from its price list. A vendor is assigned only when chosen.
  const selectItem = (key, itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (item && lines.some((l) => l.key !== key && l.itemId === item.id)) {
      toast.info(t('That item is already on the list.'));
      return;
    }
    setLines((prev) => withTrailingBlank(prev.map((l) => {
      if (l.key !== key) return l;
      if (!item) return { ...l, itemId: '' };
      return {
        ...l,
        itemId: item.id,
        description: item.name,
        sku: item.sku || '',
        uomId: l.uomId || item.stockUomId || '',
        costCenter: l.costCenter || item.defaultCostCenter || '',
        glAccount: l.glAccount || item.defaultGlAccountCode || '',
      };
    })));
    if (!item) return;
    catalogApi.listItemSuppliers(item.id).then((links) => {
      const quotes = (links || []).filter((s) => s.isActive).map((s) => newQuote({
        supplierId: s.supplierId,
        unitPrice: s.price?.amount ?? '',
      }));
      setLines((prev) => prev.map((l) => (
        l.key === key && l.itemId === item.id && l.quotes.length === 0 ? { ...l, quotes } : l
      )));
    }).catch(() => {});
  };

  // The cheapest priced quote — a hint only; assignment stays an explicit, optional choice.
  const cheapestSupplierId = (line) => {
    const priced = line.quotes.filter((q) => q.supplierId && num(q.unitPrice) != null);
    if (priced.length === 0) return '';
    return priced.reduce((a, b) => (num(a.unitPrice) <= num(b.unitPrice) ? a : b)).supplierId;
  };

  const updateQuote = (lineKey, quoteKey, patch) => {
    setLines((prev) => prev.map((l) => {
      if (l.key !== lineKey) return l;
      const quotes = l.quotes.map((q) => (q.key === quoteKey ? { ...q, ...patch } : q));
      const next = { ...l, quotes };
      // Drop the assignment if the assigned vendor's row no longer carries that supplier.
      if (next.assignedSupplierId && !quotes.some((q) => q.supplierId === next.assignedSupplierId)) {
        next.assignedSupplierId = '';
      }
      return next;
    }));
  };

  const addQuote = (lineKey) =>
    setLines((prev) => prev.map((l) => (l.key === lineKey ? { ...l, quotes: [...l.quotes, newQuote()] } : l)));

  const removeQuote = (lineKey, quoteKey) =>
    setLines((prev) => prev.map((l) => {
      if (l.key !== lineKey) return l;
      const removed = l.quotes.find((q) => q.key === quoteKey);
      const quotes = l.quotes.filter((q) => q.key !== quoteKey);
      const next = { ...l, quotes };
      if (removed && removed.supplierId === l.assignedSupplierId) next.assignedSupplierId = '';
      return next;
    }));

  // Assigning is optional and toggleable: click an assigned vendor again to clear it.
  const assignSupplier = (lineKey, supplierId) =>
    setLines((prev) => prev.map((l) => (
      l.key === lineKey ? { ...l, assignedSupplierId: l.assignedSupplierId === supplierId ? '' : supplierId } : l
    )));

  // Seed lines from par-vs-on-hand suggestions for the chosen outlet.
  const loadFromPar = async () => {
    if (!locationId) { toast.error(t('Choose an outlet first.')); return; }
    setSeeding(true);
    try {
      const sugg = await marketlistApi.getSuggestions({ locationId });
      if (!sugg || sugg.length === 0) {
        toast.info(t('No par levels configured for this outlet.'));
        return;
      }
      setLines((prev) => {
        const existing = new Set(prev.map((l) => l.itemId).filter(Boolean));
        const added = sugg
          .filter((s) => !existing.has(s.itemId))
          .map((s) => {
            const quotes = (s.quotes || []).map((q) => newQuote({
              supplierId: q.supplierId,
              unitPrice: q.unitPrice?.amount ?? '',
            }));
            return newLine({
              itemId: s.itemId,
              description: s.description,
              sku: s.sku || '',
              uomId: s.uomId || '',
              parLevel: s.parLevel,
              onHand: s.onHand,
              suggestedQuantity: s.suggestedQuantity,
              orderQuantity: s.suggestedQuantity ?? '',
              costCenter: s.costCenter || '',
              glAccount: s.glAccountCode || '',
              quotes,
            });
          });
        toast.success(t('Loaded {count} item(s) from par levels', { count: added.length }));
        return withTrailingBlank([...prev.filter((l) => !isBlankLine(l)), ...added]);
      });
    } catch (err) {
      toast.error(t('Could not load suggestions'), { description: err?.message });
    } finally {
      setSeeding(false);
    }
  };

  const money = (n) => `${currency} ${formatAmount(n)}`;
  const assignedPrice = (l) => {
    const q = l.quotes.find((x) => x.supplierId === l.assignedSupplierId);
    return q ? num(q.unitPrice) : null;
  };
  const lineTotal = (l) => {
    const p = assignedPrice(l);
    const q = num(l.orderQuantity);
    if (p == null || q == null) return null;
    return p * q;
  };
  const orderedLines = lines.filter((l) => num(l.orderQuantity) > 0);
  const grandTotal = orderedLines.reduce((sum, l) => sum + (lineTotal(l) || 0), 0);

  const buildPayload = () => ({
    title: title.trim(),
    marketDate: marketDate ? new Date(marketDate).toISOString() : null,
    locationId: locationId || null,
    currency: (currency || 'USD').toUpperCase(),
    notes: notes.trim() || null,
    lines: lines
      .filter((l) => l.itemId || l.description.trim())
      .map((l) => ({
        itemId: l.itemId || null,
        description: l.description.trim(),
        sku: l.sku?.trim() || null,
        uomId: l.uomId || null,
        parLevel: l.parLevel,
        onHand: l.onHand,
        suggestedQuantity: l.suggestedQuantity,
        orderQuantity: num(l.orderQuantity) ?? 0,
        glAccountCode: l.glAccount || null,
        costCenter: l.costCenter || null,
        notes: l.notes?.trim() || null,
        quotes: l.quotes
          .filter((q) => q.supplierId)
          .map((q) => ({
            supplierId: q.supplierId,
            unitPrice: q.unitPrice === '' ? null : Number(q.unitPrice),
            note: q.note?.trim() || null,
          })),
        assignedSupplierId: l.assignedSupplierId || null,
        assignedUnitPrice: assignedPrice(l),
      })),
  });

  const validate = (mode) => {
    if (!title.trim()) { toast.error(t('Enter a title.')); return false; }
    if (!locationId) { toast.error(t('Choose an outlet.')); return false; }
    if (mode === 'start' && orderedLines.length === 0) {
      toast.error(t('Set an order quantity on at least one line before starting purchasing.'));
      return false;
    }
    if (mode === 'finalize') {
      if (orderedLines.length === 0) { toast.error(t('Set an order quantity on at least one line before finalizing.')); return false; }
      const unassigned = orderedLines.filter((l) => !l.assignedSupplierId);
      if (unassigned.length > 0) {
        toast.error(t('Assign a vendor to every ordered line ({count} still need one).', { count: unassigned.length }));
        return false;
      }
    }
    return true;
  };

  const persist = async () => {
    const payload = buildPayload();
    return editing ? marketlistApi.update(editingId, payload) : marketlistApi.create(payload);
  };

  const save = async () => {
    if (!validate('save')) return;
    setSubmitting(true);
    try {
      const saved = await persist();
      toast.success(editing ? t('Market list updated') : t('{number} created', { number: saved.number }));
      navigate(marketListPath(saved));
    } catch (err) {
      toast.error(t('Could not save market list'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const startPurchasing = async () => {
    if (!validate('start')) return;
    setSubmitting(true);
    try {
      const saved = await persist();
      const done = await marketlistApi.startPurchasing(saved.id);
      toast.success(t('Purchasing started'));
      navigate(marketListPath(done));
    } catch (err) {
      toast.error(t('Could not start purchasing'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const openFinalize = () => {
    if (!validate('finalize')) return;
    setFinalizeOpen(true);
  };

  const doFinalize = async () => {
    setSubmitting(true);
    try {
      const saved = await persist();
      const done = await marketlistApi.finalize(saved.id, submitOnFinalize);
      const count = done?.generatedRequisitions?.length || 0;
      setFinalizeOpen(false);
      toast.success(t('Finalized into {count} requisition(s)', { count }));
      navigate(marketListPath(done));
    } catch (err) {
      toast.error(t('Could not finalize market list'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/market-list')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Market list')}
      </Button>

      <PageHeader
        title={editing ? (inProgress ? t('Edit market list (purchasing)') : t('Edit market list')) : t('New market list')}
        description={t("Build the day's order, capture vendor prices as you buy, and assign a vendor per line. A purchasing officer finalizes it into requisitions when purchasing is complete.")}
        icon={<Icon.ClipboardList size={22} />}
      />

      <div className="ml-form">
        <Card>
          <Card.Body>
            <div className="ml-form">
              <Input label={t('Title')} placeholder={t('e.g. Daily produce — Main kitchen')} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              <div className="ml-form__grid">
                <Input label={t('Market date')} type="date" value={marketDate} onChange={(e) => setMarketDate(e.target.value)} />
                <div className="ps-field">
                  <span className="ps-field__label">{t('Outlet')}</span>
                  <Select value={locationId} onChange={setLocationId} options={locationOptions} ariaLabel={t('Outlet')} searchable />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Currency')}</span>
                  <div className="ml-currency-fixed" title={t("Market lists are local purchases — the currency is the property's local currency and can't be changed.")}>
                    <span className="ml-currency-fixed__code">{currency || '—'}</span>
                    <Badge tone="neutral">{t('Local')}</Badge>
                  </div>
                </div>
              </div>
              <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("Context for the day's market run (optional)")} />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Items & vendor quotes')}</Card.Title>
            <Button variant="ghost" size="sm" leadingIcon={<Icon.Sparkles size={15} />} loading={seeding} onClick={loadFromPar}>
              {t('Load from par')}
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="ml-lines">
              {lines.map((l, idx) => {
                  const blank = isBlankLine(l);
                  if (blank) {
                    return (
                      <div className="ml-line ml-line--blank" key={l.key}>
                        <div className="ml-line__add">
                          <Icon.Plus size={16} className="ml-line__add-icon" />
                          <Combobox
                            options={itemOptions}
                            value=""
                            onChange={(v) => selectItem(l.key, v)}
                            onSearch={itemPicker.onSearchChange}
                            placeholder={t('Search the catalog to add an item…')}
                            searchPlaceholder={t('Search name or SKU…')}
                            size="sm"
                            emptyText={itemPicker.searching ? t('Searching…') : t('No catalog matches')}
                            ariaLabel={t('Add catalog item')}
                          />
                        </div>
                      </div>
                    );
                  }
                  const total = lineTotal(l);
                  const cheapestId = cheapestSupplierId(l);
                  return (
                    <div className="ml-line" key={l.key}>
                      <div className="ml-line__top">
                        <div className="ml-line__item">
                          <span className="ml-line__num">{idx + 1}</span>
                          <div className="ml-line__picker">
                            {l.itemId ? (
                              <Combobox
                                options={itemOptions}
                                value={l.itemId}
                                onChange={(v) => selectItem(l.key, v)}
                                onSearch={itemPicker.onSearchChange}
                                placeholder={t('Search catalog…')}
                                searchPlaceholder={t('Search name or SKU…')}
                                size="sm"
                                emptyText={itemPicker.searching ? t('Searching…') : t('No catalog matches')}
                                ariaLabel={t('Catalog item')}
                              />
                            ) : (
                              <div className="ml-line__name">{l.description || t('Untitled item')}</div>
                            )}
                            {l.sku && <div className="ml-line__sub">{l.sku}</div>}
                          </div>
                        </div>
                        {(l.parLevel != null || l.onHand != null) && (
                          <div className="ml-line__stats">
                            <div className="ml-stat"><span className="ml-stat__label">{t('Par')}</span><span className="ml-stat__value">{l.parLevel ?? '—'}</span></div>
                            <div className="ml-stat"><span className="ml-stat__label">{t('On hand')}</span><span className="ml-stat__value">{l.onHand ?? '—'}</span></div>
                            <div className="ml-stat"><span className="ml-stat__label">{t('Suggested')}</span><span className="ml-stat__value">{l.suggestedQuantity ?? '—'}</span></div>
                          </div>
                        )}
                        <IconButton aria-label={t('Remove item')} variant="ghost" size="icon-sm" onClick={() => removeLine(l.key)}><Icon.Trash size={15} /></IconButton>
                      </div>

                      <div className="ml-line__fields">
                        <div className="ml-field ml-field--qty">
                          <span className="ml-field__label">{t('Order qty')}</span>
                          <Input size="sm" type="number" value={l.orderQuantity} placeholder="0" onChange={(e) => updateLine(l.key, { orderQuantity: e.target.value })} />
                        </div>
                        <div className="ml-field">
                          <span className="ml-field__label">{t('Unit')}</span>
                          <Select size="sm" value={l.uomId} onChange={(v) => updateLine(l.key, { uomId: v })} options={uomOptions} ariaLabel={t('Unit')} searchable />
                        </div>
                        {(costCenters.length > 0 || glAccounts.length > 0) && (
                          <>
                            <div className="ml-field">
                              <span className="ml-field__label">{t('Cost centre')}</span>
                              <Select size="sm" value={l.costCenter} onChange={(v) => updateLine(l.key, { costCenter: v })} options={lineCostCenterOptions} ariaLabel={t('Cost centre')} searchable />
                            </div>
                            <div className="ml-field">
                              <span className="ml-field__label">{t('GL account')}</span>
                              <Select size="sm" value={l.glAccount} onChange={(v) => updateLine(l.key, { glAccount: v })} options={lineGlAccountOptions} ariaLabel={t('GL account')} searchable />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="ml-quotes">
                        <div className="ml-quotes__head">
                          <span>{t('Vendor quotes')}</span>
                          {total != null && (
                            <span className="ml-quotes__total">{t('Line total {amount}', { amount: money(total) })}</span>
                          )}
                        </div>
                        {l.quotes.length === 0 && <div className="ml-quotes__empty">{t('No quotes yet — add a vendor and price.')}</div>}
                        {l.quotes.map((q) => {
                          const assigned = l.assignedSupplierId === q.supplierId && !!q.supplierId;
                          const isCheapest = q.supplierId && q.supplierId === cheapestId && num(q.unitPrice) != null;
                          return (
                            <div className={`ml-quote ${assigned ? 'is-assigned' : ''}`} key={q.key}>
                              <div className="ml-quote__supplier">
                                <Combobox
                                  options={supplierOptions}
                                  value={q.supplierId}
                                  onChange={(v) => updateQuote(l.key, q.key, { supplierId: v })}
                                  placeholder={t('Vendor…')}
                                  searchPlaceholder={t('Search vendors…')}
                                  size="sm"
                                  emptyText={t('No vendor matches')}
                                  ariaLabel={t('Quote vendor')}
                                />
                              </div>
                              <div className="ml-quote__price">
                                <Input size="sm" type="number" value={q.unitPrice} placeholder="0.00" onChange={(e) => updateQuote(l.key, q.key, { unitPrice: e.target.value })} />
                              </div>
                              <div className="ml-quote__note">
                                <Input size="sm" value={q.note} placeholder={t('Note (optional)')} onChange={(e) => updateQuote(l.key, q.key, { note: e.target.value })} />
                              </div>
                              <div className="ml-quote__assign">
                                {isCheapest && !assigned && <Badge tone="success">{t('Cheapest')}</Badge>}
                                <Button
                                  variant={assigned ? 'primary' : 'outline'}
                                  size="sm"
                                  disabled={!q.supplierId}
                                  onClick={() => assignSupplier(l.key, q.supplierId)}
                                >
                                  {assigned ? t('Assigned') : t('Assign')}
                                </Button>
                              </div>
                              <IconButton aria-label={t('Remove quote')} variant="ghost" size="icon-sm" onClick={() => removeQuote(l.key, q.key)}><Icon.X size={14} /></IconButton>
                            </div>
                          );
                        })}
                        <Button variant="ghost" size="sm" leadingIcon={<Icon.Plus size={14} />} onClick={() => addQuote(l.key)}>{t('Add vendor quote')}</Button>
                      </div>

                      {l.assignedSupplierId && (
                        <div className="ml-line__assigned">
                          {t('Assigned to')} <strong>{supplierName(l.assignedSupplierId)}</strong>
                          {assignedPrice(l) != null && <> {t('at {price}/unit', { price: money(assignedPrice(l)) })}</>}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {orderedLines.length > 0 && (
              <div className="ml-foot">
                <span className="ml-foot__label">{t('{count} line(s) to order · est. total', { count: orderedLines.length })}</span>
                <span className="ml-foot__value">{money(grandTotal)}</span>
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="ml-form__actions">
          <Button variant="ghost" onClick={() => navigate('/market-list')} disabled={submitting}>{t('Cancel')}</Button>
          <Button variant="secondary" loading={submitting} onClick={save}>{inProgress ? t('Save') : t('Save draft')}</Button>
          {!inProgress && (
            <Button variant="secondary" loading={submitting} leadingIcon={<Icon.ShoppingCart size={15} />} onClick={startPurchasing}>{t('Start purchasing')}</Button>
          )}
          <Button variant="primary" loading={submitting} leadingIcon={<Icon.CircleCheck size={15} />} onClick={openFinalize}>{t('Finalize into requisitions')}</Button>
        </div>
      </div>

      <ConfirmDialog
        open={finalizeOpen}
        title={t('Finalize market list')}
        description={t('This creates one requisition per assigned vendor for the ordered lines. The market list becomes read-only.')}
        confirmLabel={t('Finalize')}
        tone="primary"
        loading={submitting}
        onConfirm={doFinalize}
        onCancel={() => (submitting ? null : setFinalizeOpen(false))}
      >
        <Checkbox
          checked={submitOnFinalize}
          onChange={(e) => setSubmitOnFinalize(e.target.checked)}
          label={t('Submit the requisitions for approval')}
          hint={t('Leave unchecked to create them as drafts so prices can still be refined before submitting.')}
        />
      </ConfirmDialog>
    </PageContainer>
  );
}
